import { emptySplitApi } from './baseApi';
import { broadcastCacheInvalidator } from '../utils/broadcastCacheInvalidator';
import {
  RunListResponse,
  RunDetail,
  BidRecommendation,
  ReviewRequest,
  BulkReviewRequest,
  ReportResponse,
  PublishResponse,
  AdGroupMetricsResponse,
} from '../types/bidRecommendations';

export const bidRecommendationSlice = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchBidRecommendationRuns: builder.query<
      RunListResponse,
      { page: number; pageSize: number }
    >({
      query: ({ page, pageSize }) => ({
        url: `/bid_recommendations/runs`,
        params: { page, page_size: pageSize },
      }),
      providesTags: ['BidRecommendationRuns'],
    }),

    fetchBidRecommendationRun: builder.query<RunDetail, string>({
      query: (runId) => `/bid_recommendations/runs/${runId}`,
      providesTags: (_result, _error, runId) => [
        { type: 'BidRecommendationRun', id: runId },
      ],
    }),

    reviewRecommendation: builder.mutation<
      BidRecommendation,
      { runId: string; index: number; body: ReviewRequest }
    >({
      query: ({ runId, index, body }) => ({
        url: `/bid_recommendations/runs/${runId}/recommendations/${index}/review`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { runId }) =>
        broadcastCacheInvalidator([
          'BidRecommendationRuns',
          { type: 'BidRecommendationRun', id: runId },
        ]),
    }),

    bulkReviewRecommendations: builder.mutation<
      RunDetail,
      { runId: string; body: BulkReviewRequest }
    >({
      query: ({ runId, body }) => ({
        url: `/bid_recommendations/runs/${runId}/bulk_review`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { runId }) =>
        broadcastCacheInvalidator([
          'BidRecommendationRuns',
          { type: 'BidRecommendationRun', id: runId },
        ]),
    }),

    fetchRunReport: builder.query<ReportResponse, string>({
      query: (runId) => `/bid_recommendations/runs/${runId}/report`,
    }),

    publishRecommendations: builder.mutation<PublishResponse, string>({
      query: (runId) => ({
        url: `/bid_recommendations/runs/${runId}/publish`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, runId) =>
        broadcastCacheInvalidator([
          'BidRecommendationRuns',
          { type: 'BidRecommendationRun', id: runId },
        ]),
    }),

    fetchAdGroupMetrics: builder.query<
      AdGroupMetricsResponse,
      { adGroupId: number; platform: string; days?: number }
    >({
      query: ({ adGroupId, platform, days = 30 }) => ({
        url: `/bid_recommendations/ad_group_metrics/${adGroupId}`,
        params: { platform, days },
      }),
    }),
  }),
});

export const {
  useFetchBidRecommendationRunsQuery,
  useFetchBidRecommendationRunQuery,
  useReviewRecommendationMutation,
  useBulkReviewRecommendationsMutation,
  useFetchRunReportQuery,
  useLazyFetchRunReportQuery,
  usePublishRecommendationsMutation,
  useFetchAdGroupMetricsQuery,
} = bidRecommendationSlice;
